'use client'

import { useEffect, useRef, useState } from 'react'
import LiveDealCount from './LiveDealCount'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import Navbar from './Navbar'
import { useAuth } from '../context/AuthContext'
import { useCurrency } from '@/lib/currency'
import { DealsListSkeleton } from './LoadingSkeleton'
import DealCard from './DealCard'
import DealsHero from './DealsHero'
import DealsEmptyState from './DealsEmptyState'
import DealsGuestWall from './DealsGuestWall'
import DealsActiveFilters from './DealsActiveFilters'
import DealsOpenJaw from './DealsOpenJaw'
import DealsAbout from './DealsAbout'
import DealTypeTabs from './DealTypeTabs'
import DealsStatusBanners from './DealsStatusBanners'
import DealsPopularFallback from './DealsPopularFallback'
import DealsOriginSection from './DealsOriginSection'
import DealsSearchSortBar from './DealsSearchSortBar'
import useDealsShell from '../hooks/useDealsShell'
import GuestSignupCTA from '@/components/GuestSignupCTA'
import AirportDealsPreview from '@/components/AirportDealsPreview'
import OnboardingChecklist, { isOnboardingDone } from '@/components/OnboardingChecklist'

const AuthModal = dynamic(() => import('./AuthModal'), { ssr: false })
const PreferencesPanel = dynamic(() => import('./PreferencesPanel'), { ssr: false })

const SCANNED_AIRPORTS = [
  'LHR', 'LGW', 'STN', 'MAN', 'BRS', 'EDI', 'NCL', 'LCY',
  'AMS', 'EIN', 'RTM', 'GRQ',
  'FRA', 'MUC', 'BER', 'DUS', 'HAM', 'STR', 'NRN',
  'CDG', 'ORY', 'LYS',
  'MAD', 'BCN',
  'FCO', 'MXP',
  'BRU', 'VIE', 'ZRH',
  'CPH', 'ARN', 'OSL', 'HEL',
  'DUB', 'WAW', 'PRG', 'BUD', 'LIS', 'ATH',
  'OPO', 'KRK', 'NAP', 'AGP', 'ALC', 'VLC', 'GVA', 'OTP', 'IST',
]

function timeAgo(ts) {
  if (!ts) return null
  const h = Math.floor((Date.now() - new Date(ts).getTime()) / 3600000)
  if (h < 1) return 'just now'
  return h === 1 ? '1 hour ago' : `${h} hours ago`
}


const HOW_STEPS = [
  { icon: '🔄', title: 'Scanned every hour', body: '3,000+ European and 570 intercontinental routes — checked around the clock, automatically.' },
  { icon: '📈', title: 'Compared to real price data', body: 'Each fare is checked against our own price history, fares up to 6 months ahead, and public sources like Google Flights — not cheap in general, cheap for this specific route.' },
  { icon: '🎯', title: 'Only real drops make the cut', body: 'We filter out the noise. A deal appears only when the price is meaningfully below normal.' },
  { icon: '__zero__', title: 'You pay airline prices', body: 'No markup. We link straight to the airline or Google Flights — you pay exactly what they charge.' },
]

const PLAN_DATA = [
  {
    name: 'Free', badge: null,
    monthlyEur: 0, yearlyEur: 0, yearlyTotalEur: null,
    color: 'bg-gray-50 border-gray-200 text-gray-700',
    perks: ['Limited deals', 'Rotates daily', 'European routes'],
  },
  {
    name: 'Plus', badge: 'Most popular',
    monthlyEur: 4.99, yearlyEur: 2.92, yearlyTotalEur: 35,
    color: 'bg-blue-50 border-blue-200 text-blue-800',
    perks: ['All European deals, live in app', 'Daily email digest', '5 home airports'],
  },
  {
    name: 'Pro', badge: null,
    monthlyEur: 9.99, yearlyEur: 4.08, yearlyTotalEur: 49,
    color: 'bg-indigo-600 border-indigo-600 text-white',
    perks: ['European + intercontinental deals', 'Real-time alerts', '15 airports'],
  },
]

function fmtEur(n) {
  if (n === 0) return '€0'
  return Number.isInteger(n) ? `€${n}` : `€${n.toFixed(2).replace('.', ',')}`
}

function GuestCTA({ totalDeals, onSignIn }) {
  const [yearly, setYearly] = useState(true)
  const { formatPlanPrice, currency } = useCurrency()
  const zeroIcon = { GBP: '£0', USD: '$0', CHF: 'CHF 0', SEK: 'SEK 0', NOK: 'NOK 0', DKK: 'DKK 0', TRY: '₺0', PLN: 'zł0', RON: 'lei 0', HUF: 'Ft 0', CZK: 'Kč 0' }[currency] ?? '€0'
  function approx(n) { return formatPlanPrice(n) }
  return (
    <div className="flex flex-col gap-5 h-full">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">How it works</p>
        <p className="mt-1.5 text-base font-bold text-gray-900 leading-snug">Not just cheap — genuinely below baseline.</p>
        <p className="mt-1 text-sm text-gray-500">A deal only makes the list when the fare is meaningfully lower than what that route normally costs.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-1">
        {HOW_STEPS.map(({ icon, title, body }, i) => (
          <div key={title} className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-4">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold">{i + 1}</span>
              <p className="text-xl leading-none">{icon === '__zero__' ? zeroIcon : icon}</p>
            </div>
            <p className="font-semibold text-gray-900 text-xs mb-1">{title}</p>
            <p className="text-xs text-gray-500 leading-relaxed">{body}</p>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">Plans</p>
          <div className="flex items-center gap-0.5 rounded-full bg-gray-100 p-0.5">
            <button onClick={() => setYearly(false)} className={`rounded-full px-2.5 py-1 min-h-[44px] inline-flex items-center text-[11px] font-medium transition-colors ${!yearly ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>Monthly</button>
            <button onClick={() => setYearly(true)}  className={`rounded-full px-2.5 py-1 min-h-[44px] inline-flex items-center text-[11px] font-medium transition-colors ${yearly  ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}>Yearly <span className="text-green-600">−35%</span></button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-3">
          {PLAN_DATA.map(({ name, badge, monthlyEur, yearlyEur, yearlyTotalEur, color, perks }) => (
            <div key={name} className={`relative rounded-xl border px-3 py-3 ${color}`}>
              {badge && <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-blue-600 px-2 py-0.5 text-[9px] font-bold text-white">{badge}</span>}
              <p className="font-bold text-xs mb-0.5">{name}</p>
              {(() => { const eur = yearly ? yearlyEur : monthlyEur; const h = approx(eur); return (<p className="text-base font-extrabold leading-none">{fmtEur(eur)}{h && <span className="ml-1 text-[9px] font-normal opacity-60">({h})</span>}<span className="text-[10px] font-normal opacity-70">/mo</span></p>) })()}
              {yearly && yearlyTotalEur
                ? <p className="text-[10px] opacity-60 mb-1.5 truncate">{fmtEur(yearlyTotalEur)}/yr</p>
                : <p className="text-[10px] mb-1.5">&nbsp;</p>
              }
              {perks.map(p => <p key={p} className="text-[11px] leading-snug opacity-80 mt-0.5">{p}</p>)}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-auto pt-3 border-t border-gray-100 flex items-center gap-3">
        <button
          onClick={onSignIn}
          className="flex-1 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 transition-colors"
        >
          Create free account →
        </button>
        <a href="/pricing" className="text-xs text-blue-600 hover:underline whitespace-nowrap">Full pricing & feature comparison →</a>
      </div>
      <p className="-mt-3 text-center text-xs text-gray-500">Free · No credit card needed</p>
    </div>
  )
}

const STOP_OPTS = [
  { key: 'direct', label: 'Direct' },
  { key: 'max1',   label: '≤1 stop' },
  { key: 'all',    label: 'Any' },
]

function FilterChip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border px-2.5 py-2 min-h-[44px] inline-flex items-center text-xs font-medium transition-colors ${
        active
          ? 'border-orange-400 bg-orange-50 text-orange-700'
          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  )
}

function Toggle({ value, onChange, label }) {
  return (
    <label className="flex cursor-pointer items-center gap-3">
      <div
        onClick={() => onChange(!value)}
        className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${value ? 'bg-orange-500' : 'bg-gray-200'}`}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </div>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  )
}

function QuickFilter({
  origin, airports, isPaid, onChangeOrigin,
  stopsFilter, setStopsFilter,
  weekendOnly, setWeekendOnly,
  dateFrom, setDateFrom, dateTo, setDateTo,
  maxPriceFilter, setMaxPriceFilter,
  clearAdvancedFilters, filtersCount,
}) {
  const HOME_AIRPORTS = ['AMS', 'CDG', 'LHR', 'BRU', 'FRA', 'MAD', 'BCN', 'FCO']
  const airportChips = isPaid
    ? [...new Set([origin, ...HOME_AIRPORTS])].slice(0, 10)
    : [origin]

  return (
    <div className="flex flex-col gap-4 h-full">
      <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">Filters</p>

      <div>
        <p className="mb-2 text-xs font-medium text-gray-500">Stops</p>
        <div className="flex gap-1.5">
          {STOP_OPTS.map(({ key, label }) => (
            <FilterChip key={key} active={stopsFilter === key} onClick={() => { setStopsFilter(key); localStorage.setItem('ff_stops_filter', key) }}>{label}</FilterChip>
          ))}
        </div>
      </div>

      <Toggle value={weekendOnly} onChange={setWeekendOnly} label="Weekend trips only" />

      <div>
        <p className="mb-2 text-xs font-medium text-gray-500">Travel dates</p>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="w-8 shrink-0 text-xs text-gray-500">From</span>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="flex-1 rounded-lg border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400" />
          </div>
          <div className="flex items-center gap-2">
            <span className="w-8 shrink-0 text-xs text-gray-500">To</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="flex-1 rounded-lg border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400" />
          </div>
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-medium text-gray-500">Max price</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">€</span>
          <input type="number" min="0" max="5000" step="10" value={maxPriceFilter}
            onChange={(e) => setMaxPriceFilter(e.target.value)} placeholder="Any"
            className="w-24 rounded-lg border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400" />
        </div>
      </div>

      {filtersCount > 0 && (
        <button
          onClick={() => { clearAdvancedFilters(); setStopsFilter('all'); localStorage.setItem('ff_stops_filter', 'all'); setWeekendOnly(false) }}
          className="mt-auto rounded-lg border border-red-200 bg-red-50 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100 transition-colors"
        >
          Clear all filters
        </button>
      )}
    </div>
  )
}

export default function DealsShell({ deals: initialDeals = [], source: initialSource, generatedAt: initialGeneratedAt, totalDeals, isDealsPage = false, initialOriginStats = {} }) {
  const { formatDealPrice } = useCurrency()
  const { currentUser, accessToken, signOut } = useAuth()
  const airports = [...SCANNED_AIRPORTS].sort()
  const prefAirports = airports
  const dealsRef = useRef(null)

  const [deals, setDeals] = useState(initialDeals)
  const [generatedAt, setGeneratedAt] = useState(initialGeneratedAt ?? null)
  const [dealsLoading, setDealsLoading] = useState(isDealsPage && initialDeals.length === 0)
  const [noHomeAirportDeals, setNoHomeAirportDeals] = useState(false)
  const [homeAirportCode, setHomeAirportCode] = useState(null)
  const [suggestedOrigin, setSuggestedOrigin] = useState(null)
  const [originStats, setOriginStats] = useState(initialOriginStats)
  const [yearlyDealsByOrigin, setYearlyDealsByOrigin] = useState({})
  const [historicalStats, setHistoricalStats] = useState({})
  const [apiTotal, setApiTotal] = useState(totalDeals ?? null)
  const [serverTier, setServerTier] = useState(null)
  const [serverHomeAirports, setServerHomeAirports] = useState(null)

  useEffect(() => {
    if (!isDealsPage) return
    // Gasten met SSR deals: geen herlaad nodig — selectGuestDeals geeft zelfde resultaat
    if (!accessToken && initialDeals.length > 0) return
    if (initialDeals.length === 0) setDealsLoading(true)
    const headers = {}
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    fetch('/api/deals', { headers })
      .then(r => r.json())
      .then(data => {
        setDeals(data.deals ?? [])
        if (data.generatedAt) setGeneratedAt(data.generatedAt)
        setNoHomeAirportDeals(data.noHomeAirportDeals ?? false)
        setHomeAirportCode(data.homeAirport ?? null)
        if (data.suggestedOrigin) setSuggestedOrigin(data.suggestedOrigin)
        if (data.originStats) setOriginStats(data.originStats)
        if (data.yearlyDealsByOrigin) setYearlyDealsByOrigin(data.yearlyDealsByOrigin)
        if (data.historicalStats) setHistoricalStats(data.historicalStats)
        if (data.total) setApiTotal(data.total)
        if (data.tier) setServerTier(data.tier)
        if (data.homeAirports) setServerHomeAirports(data.homeAirports)
      })
      .catch(() => {})
      .finally(() => setDealsLoading(false))
  }, [isDealsPage, accessToken])

  const s = useDealsShell({ deals, generatedAt, currentUser, airports, suggestedOrigin, serverTier, serverHomeAirports, accessToken })
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [awardWaitlistJoined, setAwardWaitlistJoined] = useState(false)

  useEffect(() => { setAwardWaitlistJoined(localStorage.getItem('ff_award_waitlist') === '1') }, [])

  const handleAwardWaitlist = () => {
    window.plausible?.('AwardWaitlist', { props: { tier: s.tier, loggedIn: !!currentUser } })
    localStorage.setItem('ff_award_waitlist', '1')
    setAwardWaitlistJoined(true)
  }

  const handleChangeOrigin = (ap) => {
    if (!ap) return
    if (!s.isPaid && ap !== s.origin) { s.setShowAirportLock(true); return }
    s.setShowAirportLock(false)
    s.setOrigin(ap)
    s.setHasOriginPref(true)
    localStorage.setItem('fairfares_origin', ap)
    if (s.destFilter.length > 0) s.updateDestFilter([])
  }

  return (
    <>
      <Navbar
        currentUser={currentUser}
        profile={s.profile}
        onSignIn={() => s.setShowModal(true)}
        onSignOut={signOut}
        onPreferences={() => s.setShowPreferences(true)}
      />

      {!isDealsPage && <DealsHero deals={deals} totalDeals={totalDeals} dealsRef={dealsRef} currentUser={currentUser} formatDealPrice={formatDealPrice} />}
      {!isDealsPage && <DealsAbout deals={deals} />}

      {isDealsPage && (
        <div className="bg-blue-600 px-4 py-4 sm:py-5">
          <div className="mx-auto max-w-5xl">
            <nav className="text-xs text-white mb-1.5" aria-label="Breadcrumb">
              <Link href="/" className="hover:text-blue-100 transition-colors">Home</Link>
              <span className="mx-1.5 text-white/60">/</span>
              <span className="text-white">Deals</span>
            </nav>
            <h1 className="text-xl sm:text-2xl font-bold text-white leading-tight">Flight deals</h1>
            <p className="text-xs text-white mt-1"><LiveDealCount fallback={null} className="text-white font-semibold" /> routes with deals · monitored 24/7 across airlines &amp; booking sites</p>
          </div>
        </div>
      )}

      <main ref={dealsRef} className="min-h-screen bg-gray-50 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          {!isDealsPage && (
            <header className="mb-8 text-center">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">Deals</h1>
              <p className="mt-2 text-gray-500">Genuine flight deals, found by AI. Flights monitored around the clock<LiveDealCount fallback={null} />.</p>
              <p className="mt-3 text-xs text-gray-500 max-w-lg mx-auto leading-relaxed">
                There are hundreds of sites to book flights. None of them hunt for deals. Every price below has been verified against months of history — not just cheap, but genuinely below normal.
              </p>
            </header>
          )}

          {!currentUser && (
            <AirportDealsPreview suggestedOrigin={suggestedOrigin} originStats={originStats} yearlyDealsByOrigin={yearlyDealsByOrigin} historicalStats={historicalStats} tier={s.tier ?? 'guest'} />
          )}

          {!currentUser && <GuestSignupCTA headline="See some deals. Sign up free to unlock many more." ariaLabel="Sign up free to see flight deals" />}

          <DealsStatusBanners
            isOffline={s.isOffline}
            showInstallBanner={s.showInstallBanner} installPrompt={s.installPrompt}
            setInstallPrompt={s.setInstallPrompt} setShowInstallBanner={s.setShowInstallBanner}
            showSubscribedBanner={s.showSubscribedBanner} setShowSubscribedBanner={s.setShowSubscribedBanner}
            currentUser={currentUser} setShowModal={s.setShowModal}
            isPaid={s.isPaid} dealTypeTab={s.dealTypeTab}
            notifPermission={s.notifPermission} requestPushPermission={s.requestPushPermission}
          />

          {s.isPaid ? (
            <>
              <button
                onClick={() => setFiltersOpen(v => !v)}
                className="mb-3 min-h-[44px] flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg className={`w-3.5 h-3.5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                {filtersOpen ? 'Hide filters' : 'Filter & sort'}
              </button>
              {filtersOpen && (
                <>
                  <DealsOriginSection
                    currentUser={currentUser} airports={airports} origin={s.origin}
                    isPaid={s.isPaid} AIRPORT_LIMIT={s.AIRPORT_LIMIT}
                    showAirportLock={s.showAirportLock} setShowAirportLock={s.setShowAirportLock}
                    liveDealCounts={s.liveDealCounts} allDestinations={s.allDestinations}
                    destFilter={s.destFilter} updateDestFilter={s.updateDestFilter}
                    flexDays={s.flexDays} homeAirports={s.homeAirports}
                    selectedOriginAirports={s.selectedOriginAirports}
                    setSelectedOriginAirports={s.setSelectedOriginAirports}
                    onChangeOrigin={handleChangeOrigin}
                  />
                  <QuickFilter
                    origin={s.origin} airports={airports} isPaid={s.isPaid} onChangeOrigin={handleChangeOrigin}
                    stopsFilter={s.stopsFilter} setStopsFilter={s.setStopsFilter}
                    weekendOnly={s.weekendOnly} setWeekendOnly={s.setWeekendOnly}
                    dateFrom={s.dateFrom} setDateFrom={s.setDateFrom}
                    dateTo={s.dateTo} setDateTo={s.setDateTo}
                    maxPriceFilter={s.maxPriceFilter} setMaxPriceFilter={s.setMaxPriceFilter}
                    clearAdvancedFilters={s.clearAdvancedFilters} filtersCount={s.filtersCount}
                  />
                  <DealTypeTabs deals={deals} dealTypeTab={s.dealTypeTab} setDealTypeTab={s.setDealTypeTab} />
                  <DealsSearchSortBar
                    searchText={s.searchText} setSearchText={s.setSearchText}
                    sortBy={s.sortBy} setSortBy={s.setSortBy}
                    regionFilter={s.regionFilter} setRegionFilter={s.setRegionFilter} regionCounts={s.regionCounts}
                  />
                </>
              )}
            </>
          ) : (
            <div className="mb-3 flex items-center gap-2 text-sm text-gray-500">
              <span className="text-base">🔒</span>
              <span>Filter or sort —{' '}
                <a href="/pricing" className="text-blue-700 hover:underline font-medium">upgrade to Plus</a>
              </span>
            </div>
          )}

          <div>
            <div className="min-w-0">
              <DealsActiveFilters
                dealTypeTab={s.dealTypeTab} setDealTypeTab={s.setDealTypeTab}
                weekendOnly={s.weekendOnly} setWeekendOnly={s.setWeekendOnly}
                stopsFilter={s.stopsFilter} setStopsFilter={s.setStopsFilter}
                destFilter={s.destFilter} updateDestFilter={s.updateDestFilter}
                filterAirports={s.filterAirports} setFilterAirports={s.setFilterAirports}
                airlineFilter={s.airlineFilter} setAirlineFilter={s.setAirlineFilter}
                searchText={s.searchText} setSearchText={s.setSearchText}
                dateFrom={s.dateFrom} setDateFrom={s.setDateFrom}
                dateTo={s.dateTo} setDateTo={s.setDateTo}
                maxPriceFilter={s.maxPriceFilter} setMaxPriceFilter={s.setMaxPriceFilter}
              />

              {s.dealTypeTab === 'award' ? (
                <div className="py-12 text-center">
                  <div className="text-5xl mb-5">🎫</div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Award deals — coming soon</h2>
                  <p className="text-gray-500 text-sm max-w-sm mx-auto mb-3">
                    We're building tracking for award flights — Flying Blue, Avios, Amex Membership Rewards, and more.
                    Same sharp eye for deals, but for your points and miles.
                  </p>
                  <p className="text-xs text-gray-500 max-w-xs mx-auto mb-8">
                    Think: <span className="font-medium text-gray-600">AMS → JFK for 25,000 Flying Blue miles</span> — deals you'd normally have to hunt down yourself.
                  </p>
                  {awardWaitlistJoined ? (
                    <p className="inline-flex items-center gap-2 rounded-full bg-green-50 px-5 py-2.5 text-sm font-semibold text-green-700">
                      ✓ We'll notify you when it's live
                    </p>
                  ) : (
                    <button
                      onClick={handleAwardWaitlist}
                      className="rounded-full bg-violet-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 transition-colors shadow-sm"
                    >
                      Notify me
                    </button>
                  )}
                </div>
              ) : (
              <>
              {noHomeAirportDeals && homeAirportCode && currentUser && (
                <p className="mb-3 text-xs text-gray-500">No deals from {homeAirportCode} right now.</p>
              )}

              {!currentUser ? (
                <p className="mb-3 text-sm text-gray-500">
                  {(() => {
                    const dodCount = s.dealOfDay && !s.dealOfDay._locked ? 1 : 0
                    const shown = s.visible.length + dodCount
                    return <>Showing <span className="font-semibold text-gray-900">{shown}</span> of{' '}
                      <span className="font-semibold text-gray-900">{apiTotal ?? '600+'}</span> deals —{' '}
                      <button onClick={() => s.setShowModal(true)} className="text-blue-600 hover:underline font-medium">create a free account to see all</button></>
                  })()}
                </p>
              ) : !s.isPaid ? (
                <p className="mb-3 text-sm text-gray-500">
                  <span className="font-semibold text-gray-900">{apiTotal ?? s.sorted.length}</span> deals available —{' '}
                  showing <span className="font-semibold text-gray-900">{s.visible.length + (s.dealOfDay && !s.dealOfDay._locked ? 1 : 0)}</span> for your free plan ·{' '}
                  <a href="/pricing" className="text-blue-600 hover:underline font-medium">upgrade to see all</a>
                </p>
              ) : (
                <p className="mb-3 text-sm text-gray-500">
                  <span className="font-semibold text-gray-900">{s.filteredDealsCount}</span>{' '}
                  deal{s.filteredDealsCount !== 1 ? 's' : ''} found
                  {apiTotal && s.filteredDealsCount !== apiTotal && <span className="ml-1 text-xs text-gray-500">· {apiTotal} total available</span>}
                  {' · '}<a href="/app" className="text-blue-700 hover:text-blue-900 font-medium">My FairFares →</a>
                </p>
              )}



              {(dealsLoading || s.isTransitioning) ? (
                <DealsListSkeleton count={Math.max(s.visible.length, 8)} />
              ) : (
                <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {!s.isTransitioning && s.dealOfDay && !s.dealOfDay._locked && (
                    <li key={s.dealOfDay.id}>
                      <DealCard deal={s.dealOfDay} sparklinePrices={s.historyMap[`${s.dealOfDay.origin}-${s.dealOfDay.destination}`] ?? null} lastVisit={s.lastVisit} tier={s.tier} showGoogleFlights={false} locked={false} isDealOfHour aboveFold={true} homeAirports={s.homeAirports} />
                    </li>
                  )}
                  {s.visible.map((deal, idx) => (
                    <li key={deal.id}>
                      <DealCard deal={deal} isFlexMatch={deal.isFlexMatch} sparklinePrices={s.historyMap[`${deal.origin}-${deal.destination}`] ?? null} lastVisit={s.lastVisit} tier={s.tier} showGoogleFlights={false} locked={!!deal._locked} priceWatchMatch={!!deal.isPriceWatchMatch} showGradient={s._dealGradients.get(deal.id) ?? false} homeAirports={s.homeAirports} aboveFold={idx < 4} />
                    </li>
                  ))}
                </ul>
              )}

              {!s.isTransitioning && s.visible.length === 0 && !(s.dealOfDay && !s.dealOfDay._locked) && !(currentUser && s.sorted.length === 0 && !s.weekendOnly && s.destFilter.length === 0) && (
                <DealsEmptyState
                  origin={s.origin} destFilter={s.destFilter} weekendOnly={s.weekendOnly} stopsFilter={s.stopsFilter}
                  setWeekendOnly={s.setWeekendOnly} setStopsFilter={s.setStopsFilter} updateDestFilter={s.updateDestFilter}
                  isPaid={s.isPaid} dealCounts={s.dealCounts} airports={airports} currentUser={currentUser}
                  regionFilter={s.regionFilter} lockedInRegion={s.lockedInRegion}
                  setShowModal={s.setShowModal} onSwitchAirport={(ap) => { s.setOrigin(ap); s.setHasOriginPref(true); localStorage.setItem('fairfares_origin', ap) }}
                />
              )}

              {!s.isTransitioning && currentUser && s.sorted.length === 0 && !s.weekendOnly && s.destFilter.length === 0 && (
                <DealsPopularFallback
                  deals={deals}
                  origin={s.origin}
                  dealCounts={s.dealCounts}
                  airports={airports}
                  onSwitchAirport={(ap) => { s.setOrigin(ap); s.setHasOriginPref(true); localStorage.setItem('fairfares_origin', ap) }}
                  formatDealPrice={formatDealPrice}
                  tier={s.tier}
                  lastVisit={s.lastVisit}
                  historyMap={s.historyMap}
                />
              )}

              {!s.isTransitioning && (
                <DealsGuestWall hidden={s.hidden} _dealGradients={s._dealGradients} setShowModal={s.setShowModal} totalLocked={s.sorted.filter(d => d._locked).length} />
              )}

              {!s.isTransitioning && s.hasMore && (
                <div className="mt-8 text-center">
                  <button
                    onClick={() => s.setVisibleCount((v) => v + 12)}
                    className="rounded-xl bg-blue-600 px-8 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
                  >
                    Load more deals · {s.sorted.length - s.visibleCount} remaining
                  </button>
                </div>
              )}

              {/* DealsOpenJaw hidden — needs one-way pricing data */}

              {!currentUser && <GuestSignupCTA headline="✈️ Found a deal you like? Sign up free to unlock all deals and set price alerts." ariaLabel="Sign up free to unlock all deals and alerts" />}

              </>
              )}
            </div>
          </div>
        </div>
      </main>

      {s.showModal && <AuthModal onClose={() => s.setShowModal(false)} />}
      {s.showPreferences && currentUser && (
        <PreferencesPanel
          currentUser={currentUser}
          profile={s.profile}
          airports={prefAirports}
          onClose={() => s.setShowPreferences(false)}
          onSaved={(updates) => s.setProfile((p) => ({ ...p, ...updates }))}
        />
      )}
    </>
  )
}

